import * as React from 'react';

import {
  Button,
  getEstimatedTextWidth,
  TextField,
  VirtualKeyboardWrapper,
} from '@rematter/ui-web';
import { LucideArrowUp, LucideCircleX, LucideSearch } from 'lucide-react';

import { InventoryItemButton } from '../InventoryItemButton';

import { InventoryItemsModalMode } from './_InventoryItemsModal';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';

export type MaterialSelectionModalHeaderProps = {
  searchInputRef: React.Ref<HTMLDivElement>;
  selectedMaterialGroupId: string | null;
  selectedItemId?: string | null;
  onNavigateUp: VoidFunction;
  onSelectMaterialGroup: (materialGroupId: string) => void;
  onSelectItem?: (itemId: string) => void;
  searchTerm: string;
  itemGroups: InventoryItemRecord[];
  inventoryItems?: InventoryItemRecord[];
  inventoryTagOptionsByItemId?: Map<string, unknown[]>;
  onChangeInput: (value: string) => void;
  clearSearchTerm: VoidFunction;
  autoFocus?: boolean;
  mode?: InventoryItemsModalMode;
};

type NavigateUpButtonProps = {
  onPress: VoidFunction;
};

const NavigateUpButton = ({ onPress }: NavigateUpButtonProps) => (
  <Button
    variant="outline"
    color="neutral"
    className="@NavigateUpButton border-neutral p-3 text-neutral"
    onPress={onPress}
  >
    <LucideArrowUp size={20} className="opacity-60" />
  </Button>
);

type HeaderItemLevelProps = {
  siblingItems: InventoryItemRecord[];
  onNavigateUp: VoidFunction;
  onSelectItem?: (itemId: string) => void;
};

const HeaderItemLevel = ({
  siblingItems,
  onNavigateUp,
  onSelectItem,
}: HeaderItemLevelProps) => (
  <>
    <NavigateUpButton onPress={onNavigateUp} />
    <div className="flex w-0 flex-grow gap-2">
      {siblingItems.map((item) => (
        <InventoryItemButton
          key={item.id}
          inventoryItem={item}
          isSelected={false}
          onPress={() => onSelectItem?.(item.id)}
          style={{
            flexGrow: getEstimatedTextWidth(item.name),
          }}
          overrideGroupStyle
          align="left"
          variant="subtle"
        />
      ))}
    </div>
  </>
);

type HeaderGroupLevelProps = {
  selectedMaterialGroupId: string;
  itemGroups: InventoryItemRecord[];
  onNavigateUp: VoidFunction;
  onSelectMaterialGroup: (materialGroupId: string) => void;
};

const HeaderGroupLevel = ({
  selectedMaterialGroupId,
  itemGroups,
  onNavigateUp,
  onSelectMaterialGroup,
}: HeaderGroupLevelProps) => {
  const otherGroups = itemGroups.filter(
    (group) => group.id !== selectedMaterialGroupId,
  );

  return (
    <>
      <NavigateUpButton onPress={onNavigateUp} />
      <div className="flex w-0 flex-grow gap-2">
        {otherGroups.map((group) => (
          <InventoryItemButton
            key={group.id}
            inventoryItem={group}
            isSelected={false}
            onPress={() => onSelectMaterialGroup(group.id)}
            style={{
              flexGrow: getEstimatedTextWidth(group.name),
            }}
            overrideGroupStyle={false}
          />
        ))}
      </div>
    </>
  );
};

type HeaderRootLevelProps = {
  title: string;
  searchInputRef: React.Ref<HTMLDivElement>;
  searchTerm: string;
  onChangeInput: (value: string) => void;
  clearSearchTerm: VoidFunction;
  autoFocus: boolean;
};

const HeaderRootLevel = ({
  title,
  searchInputRef,
  searchTerm,
  onChangeInput,
  clearSearchTerm,
  autoFocus,
}: HeaderRootLevelProps) => {
  const t = useTranslations();

  return (
    <>
      <span className="pl-3 text-lg font-semibold">{title}</span>
      <div className="relative h-full">
        <VirtualKeyboardWrapper value={searchTerm} onChange={onChangeInput}>
          <TextField
            ref={searchInputRef}
            prefix={<LucideSearch size={20} />}
            suffix={
              searchTerm.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="!absolute !inset-y-0 !right-3 !p-0"
                  preventFocusOnPress
                  onPress={clearSearchTerm}
                  icon={<LucideCircleX size={20} className="opacity-60" />}
                />
              )
            }
            inputMode="none"
            className="h-full w-[350px] rounded-md bg-white placeholder:text-neutral focus:border-primary focus:ring-2 disabled:opacity-50"
            placeholder={t.inventoryItemsModal.searchPlaceholder}
            autoFocus={autoFocus}
          />
        </VirtualKeyboardWrapper>
      </div>
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ MATERIAL LIST HEADER ⚛️ ⚛️ ⚛️
//
export const MaterialListHeader = ({
  selectedMaterialGroupId,
  selectedItemId,
  searchInputRef,
  searchTerm,
  itemGroups,
  inventoryItems,
  inventoryTagOptionsByItemId,
  clearSearchTerm,
  onChangeInput,
  onNavigateUp,
  onSelectMaterialGroup,
  onSelectItem,
  autoFocus = false,
  mode = 'items',
}: MaterialSelectionModalHeaderProps) => {
  const t = useTranslations();
  const isTagsMode = mode === 'tags';

  const title = isTagsMode
    ? t.inventoryItemsModal.addTags
    : t.inventoryItemsModal.addItems;

  const siblingItems = React.useMemo(() => {
    if (!isTagsMode || !selectedItemId || !selectedMaterialGroupId) return [];
    return (inventoryItems ?? []).filter(
      (item) =>
        item.itemGroupId === selectedMaterialGroupId &&
        item.id !== selectedItemId &&
        inventoryTagOptionsByItemId?.has(item.id),
    );
  }, [
    isTagsMode,
    selectedItemId,
    selectedMaterialGroupId,
    inventoryItems,
    inventoryTagOptionsByItemId,
  ]);

  const isAtItemLevel = isTagsMode && selectedItemId;
  const isAtGroupLevel = selectedMaterialGroupId && !isAtItemLevel;

  if (isAtItemLevel) {
    return (
      <div className="@MaterialListHeader flex w-full items-center justify-between gap-5">
        <HeaderItemLevel
          siblingItems={siblingItems}
          onNavigateUp={onNavigateUp}
          onSelectItem={onSelectItem}
        />
        <div className="flex gap-3" />
      </div>
    );
  }

  if (isAtGroupLevel) {
    return (
      <div className="@MaterialListHeader flex w-full items-center justify-between gap-5">
        <HeaderGroupLevel
          selectedMaterialGroupId={selectedMaterialGroupId}
          itemGroups={itemGroups}
          onNavigateUp={onNavigateUp}
          onSelectMaterialGroup={onSelectMaterialGroup}
        />
        <div className="flex gap-3" />
      </div>
    );
  }

  return (
    <div className="@MaterialListHeader flex w-full items-center justify-between gap-5">
      <HeaderRootLevel
        title={title}
        searchInputRef={searchInputRef}
        searchTerm={searchTerm}
        onChangeInput={onChangeInput}
        clearSearchTerm={clearSearchTerm}
        autoFocus={autoFocus}
      />
      <div className="flex gap-3" />
    </div>
  );
};
